// ═══════════════════════════════════════════════════════════════════
//  modules/tickets/TicketDetail.js
//  TicketDetail
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { WikiLies } from "../wiki/WikiLies";
import { AttachmentsList } from "./AttachmentsList";
import { PRIORITY_COLORS, STATUS_COLORS } from "./constantes";

export function TicketDetail({ ticketId, token, onBack, onUpdated }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const messagesContainerRef = useRef(null);
  const prevMessagesCount = useRef(0);

  const loadTicket = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/tickets/${ticketId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    setData(json);
  }, [ticketId, token]);

  useEffect(() => {
    loadTicket();
    const interval = setInterval(loadTicket, 4000);
    return () => clearInterval(interval);
  }, [ticketId, loadTicket]);

  useEffect(() => {
    if (!data) return;
    const count = data.messages?.length || 0;
    if (count > prevMessagesCount.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    prevMessagesCount.current = count;
  }, [data, loadTicket]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    await fetch(`${API_BASE}/api/tickets/${ticketId}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message: reply }),
    });
    setReply("");
    setSending(false);
    loadTicket();
    onUpdated();
  };

  const callAction = async (path, method, body) => {
    setActionLoading(path);
    try {
      await fetch(`${API_BASE}/api/tickets/${ticketId}${path}`, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: body ? JSON.stringify(body) : undefined,
      });
      await loadTicket();
      onUpdated();
    } catch (err) {
      console.error("Erreur action ticket", err);
    }
    setActionLoading(null);
  };

  const handleClaim = () => callAction("/claim", "POST");
  const handlePriority = () => callAction("/priority", "PATCH", { priorite: data.ticket.priorite === "haute" ? "normale" : "haute" });
  const handleFreeze = () => callAction("/freeze", "PATCH");
  const handleClose = () => {
    const raison = window.prompt("Raison de fermeture (optionnel) :", "");
    if (raison === null) return;
    callAction("/status", "PATCH", { status: "ferme", raison: raison || null });
  };

  if (!data) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  const { ticket, messages } = data;
  const statusInfo = STATUS_COLORS[ticket.statut] || STATUS_COLORS.ouvert;
  const priorityInfo = PRIORITY_COLORS[ticket.priorite] || PRIORITY_COLORS.normale;
  const ticketFerme = ticket.statut === "ferme";

  const ACTION_BUTTONS = [
    { key: "claim", icon: "🤝", label: "Prendre en charge", onClick: handleClaim, color: THEME.accent, bg: THEME.accentDim },
    { key: "priority", icon: "🔺", label: ticket.priorite === "haute" ? "Priorité normale" : "Priorité haute", onClick: handlePriority, color: THEME.amber, bg: THEME.amberDim },
    { key: "freeze", icon: ticket.gele ? "🔥" : "❄️", label: ticket.gele ? "Dégeler" : "Geler", onClick: handleFreeze, color: THEME.purple, bg: THEME.purpleDim },
    { key: "close", icon: "🔒", label: "Fermer", onClick: handleClose, color: THEME.red, bg: THEME.redDim },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13 }}>← Retour</button>
        <Badge text={statusInfo.label} bg={statusInfo.bg} color={statusInfo.text} />
        <Badge text={ticket.priorite} bg={priorityInfo.bg} color={priorityInfo.text} />
        {ticket.gele && <Badge text="❄️ Gelé" bg={THEME.purpleDim} color={THEME.purple} />}
        {ticket.staff_id && <Badge text={`👤 ${ticket.staff_username || ticket.staff_id}`} bg={THEME.emeraldDim} color={THEME.emerald} />}
      </div>
      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 20 }}>
        <div style={{ color: THEME.text, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{ticket.categorie}</div>
        <div style={{ color: THEME.textMuted, fontSize: 12, marginBottom: 8 }}>Discord ID {ticket.discord_user_id} · {new Date(ticket.ouvert_le).toLocaleString("fr")}</div>
        {ticket.description && (
          <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: THEME.text, fontSize: 13 }}>
            📝 {ticket.description}
          </div>
        )}
        <AttachmentsList ticketId={ticketId} token={token} />
        <WikiLies token={token} type="ticket" id={ticketId} categorie={ticket.categorie} />
        <div ref={messagesContainerRef} style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16, maxHeight: 300, overflowY: "auto" }}>
          {messages.length === 0 && <div style={{ color: THEME.textDim, fontSize: 13 }}>Aucun message pour l'instant.</div>}
          {messages.map((m) => (
            <div key={m.id} style={{ background: m.is_staff ? THEME.accentDim : THEME.surface, border: `1px solid ${m.is_staff ? THEME.accent : THEME.border}`, borderRadius: 8, padding: "10px 14px", alignSelf: m.is_staff ? "flex-end" : "flex-start", maxWidth: "75%" }}>
              <div style={{ color: m.is_staff ? THEME.accent : THEME.gold, fontWeight: 700, fontSize: 11, marginBottom: 4 }}>{m.author_name} {m.is_staff && "· Staff"}</div>
              <div style={{ color: THEME.text, fontSize: 13 }}>{m.content}</div>
            </div>
          ))}
        </div>
        {!ticketFerme && (
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Répondre au joueur..."
              style={{ flex: 1, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "8px 12px", color: THEME.text, fontSize: 13, outline: "none" }} />
            <button onClick={sendReply} disabled={sending} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 6, padding: "8px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Envoyer</button>
          </div>
        )}
        {!ticketFerme && (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 8 }}>
            {ACTION_BUTTONS.map((a) => (
              <button key={a.key} onClick={a.onClick} disabled={actionLoading === `/${a.key}`}
                style={{ background: a.bg, border: `1px solid ${a.color}`, color: a.color, borderRadius: 6, padding: "8px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, opacity: actionLoading ? 0.6 : 1 }}>
                <span style={{ fontSize: 16 }}>{a.icon}</span>{a.label}
              </button>
            ))}
          </div>
        )}
        {ticketFerme && <div style={{ color: THEME.textMuted, fontSize: 12, fontStyle: "italic" }}>Ce ticket est fermé. {ticket.raison_fermeture && `Raison : ${ticket.raison_fermeture}`}</div>}
      </div>
    </div>
  );
}
